import React, { useEffect, useRef, useState, useCallback } from 'react';
import { loadPronunciationArrayBuffer } from '../services/pronunciationAudioService';
import T from './T';

interface AudioWaveformComparatorProps {
  text: string;
  langLocale: string;
  audioUrl?: string | null;
  userAudioBlob: Blob | null;
  userAudioUrl: string | null;
  isTtsPlaying?: boolean;
  isUserAudioPlaying?: boolean;
}

export const extractAudioWaveform = (buffer: AudioBuffer, samplesCount: number = 80): number[] => {
  const rawData = buffer.getChannelData(0);
  const totalSamples = rawData.length;
  if (totalSamples === 0) return new Array(samplesCount).fill(0.05);

  // 1. Trim initial and trailing silence (threshold = 0.015)
  const silenceThreshold = 0.015;
  let startIdx = 0;
  while (startIdx < totalSamples && Math.abs(rawData[startIdx]) < silenceThreshold) {
    startIdx++;
  }

  let endIdx = totalSamples - 1;
  while (endIdx > startIdx && Math.abs(rawData[endIdx]) < silenceThreshold) {
    endIdx--;
  }

  if (startIdx >= endIdx) {
    startIdx = 0;
    endIdx = totalSamples - 1;
  }

  const activeLength = endIdx - startIdx + 1;
  const blockSize = Math.floor(activeLength / samplesCount);
  if (blockSize <= 0) return new Array(samplesCount).fill(0.1);

  const waveform: number[] = [];
  let maxRms = 0;

  for (let i = 0; i < samplesCount; i++) {
    const blockStart = startIdx + i * blockSize;
    let sumSquares = 0;
    const count = Math.min(blockSize, totalSamples - blockStart);

    for (let j = 0; j < count; j++) {
      const val = rawData[blockStart + j];
      sumSquares += val * val;
    }

    const rms = Math.sqrt(sumSquares / Math.max(1, count));
    if (rms > maxRms) maxRms = rms;
    waveform.push(rms);
  }

  // Normalize RMS energy to 0.0 - 1.0 scale
  if (maxRms > 0) {
    return waveform.map(val => Math.max(0.05, Number((val / maxRms).toFixed(3))));
  }

  return new Array(samplesCount).fill(0.1);
};

export const AudioWaveformComparator: React.FC<AudioWaveformComparatorProps> = ({
  text,
  langLocale,
  audioUrl,
  userAudioBlob,
  userAudioUrl,
  isTtsPlaying,
  isUserAudioPlaying
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [refWaveform, setRefWaveform] = useState<number[] | null>(null);
  const [userWaveform, setUserWaveform] = useState<number[] | null>(null);
  const [loadingRef, setLoadingRef] = useState<boolean>(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        audioCtxRef.current = new AudioCtx();
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, []);

  // Fetch Reference Audio & Decode to Waveform Buffer
  useEffect(() => {
    let isMounted = true;
    if (!text || !text.trim()) return;

    const fetchRefWaveform = async () => {
      setLoadingRef(true);
      const audioCtx = getAudioContext();

      // 1. Primary Strategy: Try R2 pre-recorded audio URL first
      if (audioUrl && audioCtx) {
        try {
          const arrayBuf = await loadPronunciationArrayBuffer(audioUrl);
          if (arrayBuf) {
            const audioBuffer = await audioCtx.decodeAudioData(arrayBuf.slice(0));
            const points = extractAudioWaveform(audioBuffer, 80);
            if (isMounted) {
              setRefWaveform(points);
              setLoadingRef(false);
              return;
            }
          }
        } catch (r2Err) {
          console.warn('[Waveform] R2 pre-recorded decoding failed, attempting fallback:', r2Err);
        }
      }

      // 2. Secondary Strategy: Fall back to Google Translate TTS audio buffer
      try {
        const cleanLang = (langLocale || 'en-US').split('-')[0].toLowerCase();
        const ttsUrl = `https://translate.google.com/translate_tts?ie=UTF-8&q=${encodeURIComponent(text)}&tl=${cleanLang}&client=tw-ob`;

        const res = await fetch(ttsUrl);
        if (!res.ok) throw new Error(`TTS Fetch error: ${res.status}`);

        const arrayBuf = await res.arrayBuffer();
        if (!audioCtx) throw new Error('No AudioContext available');

        const audioBuffer = await audioCtx.decodeAudioData(arrayBuf);
        const points = extractAudioWaveform(audioBuffer, 80);

        if (isMounted) {
          setRefWaveform(points);
        }
      } catch (err) {
        console.warn('[Waveform] Fallback synthetic envelope for ref text:', err);
        // 3. Tertiary Strategy: Synthetic envelope fallback based on text length & word stress
        const words = text.split(/\s+/).filter(Boolean);
        const pointsCount = 80;
        const synthetic: number[] = [];
        for (let i = 0; i < pointsCount; i++) {
          const norm = i / pointsCount;
          const envelope = Math.sin(norm * Math.PI);
          const ripple = 0.3 * Math.sin(norm * Math.PI * (words.length * 2));
          const val = Math.max(0.08, Math.min(1.0, envelope * 0.7 + Math.abs(ripple)));
          synthetic.push(Number(val.toFixed(3)));
        }
        if (isMounted) {
          setRefWaveform(synthetic);
        }
      } finally {
        if (isMounted) setLoadingRef(false);
      }
    };

    fetchRefWaveform();
    return () => { isMounted = false; };
  }, [text, langLocale, audioUrl, getAudioContext]);

  // Process User Audio Recording Blob / URL
  useEffect(() => {
    let isMounted = true;

    const processUserBlob = async () => {
      if (!userAudioBlob && !userAudioUrl) {
        setUserWaveform(null);
        return;
      }

      try {
        const audioCtx = getAudioContext();
        if (!audioCtx) return;

        let arrayBuf: ArrayBuffer | null = null;
        if (userAudioBlob) {
          arrayBuf = await userAudioBlob.arrayBuffer();
        } else if (userAudioUrl) {
          const res = await fetch(userAudioUrl);
          arrayBuf = await res.arrayBuffer();
        }

        if (arrayBuf) {
          const audioBuffer = await audioCtx.decodeAudioData(arrayBuf);
          const points = extractAudioWaveform(audioBuffer, 80);
          if (isMounted) {
            setUserWaveform(points);
          }
        }
      } catch (err) {
        console.warn('[Waveform] Failed to decode user recording audio:', err);
      }
    };

    processUserBlob();
    return () => { isMounted = false; };
  }, [userAudioBlob, userAudioUrl, getAudioContext]);

  // Draw Canvas Waveform Overlay
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const width = container.clientWidth || 320;
    const height = 75;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const numBars = 80;
    const gap = 2;
    const barWidth = Math.max(2, (width - (numBars - 1) * gap) / numBars);
    const centerY = height / 2;

    // 1. Draw Reference Waveform (Native - Cyan / Emerald 40% Opacity)
    if (refWaveform && refWaveform.length > 0) {
      for (let i = 0; i < numBars; i++) {
        const val = refWaveform[i] || 0.05;
        const barH = Math.max(4, val * (height - 12));
        const x = i * (barWidth + gap);
        const y = centerY - barH / 2;

        ctx.fillStyle = isTtsPlaying ? 'rgba(141, 175, 136, 0.8)' : 'rgba(141, 175, 136, 0.35)';
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x, y, barWidth, barH, 2);
        } else {
          ctx.rect(x, y, barWidth, barH);
        }
        ctx.fill();

        // Top accent line for native reference
        ctx.fillStyle = isTtsPlaying ? '#8E9668' : 'rgba(157, 189, 152, 0.6)';
        ctx.fillRect(x, y, barWidth, 1.5);
      }
    }

    // 2. Draw User Waveform (Recorded Voice - Amber / Gold contrast overlay)
    if (userWaveform && userWaveform.length > 0) {
      for (let i = 0; i < numBars; i++) {
        const val = userWaveform[i] || 0.05;
        const barH = Math.max(4, val * (height - 12));
        const x = i * (barWidth + gap);
        const y = centerY - barH / 2;

        const isPeakAligned = refWaveform && refWaveform[i] && Math.abs(val - refWaveform[i]) < 0.25 && val > 0.4;

        ctx.fillStyle = isUserAudioPlaying
          ? 'rgba(201, 166, 90, 0.95)'
          : (isPeakAligned ? 'rgba(214, 186, 118, 0.85)' : 'rgba(184, 148, 76, 0.65)');
        
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
          ctx.roundRect(x + 0.5, y, Math.max(1, barWidth - 1), barH, 2);
        } else {
          ctx.rect(x + 0.5, y, Math.max(1, barWidth - 1), barH);
        }
        ctx.fill();

        // Highlight matching peak / emphasis alignment
        if (isPeakAligned) {
          ctx.fillStyle = '#B08D57';
          ctx.beginPath();
          ctx.arc(x + barWidth / 2, y - 3, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }, [refWaveform, userWaveform, isTtsPlaying, isUserAudioPlaying]);

  useEffect(() => {
    drawWaveform();
    window.addEventListener('resize', drawWaveform);
    return () => window.removeEventListener('resize', drawWaveform);
  }, [drawWaveform]);

  return (
    <div ref={containerRef} className="w-full max-w-md mx-auto my-3 px-4 flex flex-col items-center gap-1.5">
      <div className="w-full bg-slate-950/40 dark:bg-slate-950/60 rounded-2xl p-2.5 border border-slate-800/80 flex flex-col items-center justify-center relative overflow-hidden min-h-[75px]">
        {loadingRef && !refWaveform && (
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-500 animate-pulse">
            Carregando onda de áudio nativa...
          </div>
        )}
        <canvas ref={canvasRef} className="w-full block" />
      </div>

      {/* Waveform Legend Indicators */}
      <div className="flex items-center justify-between w-full px-2 text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-400">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-ok/50 border border-ok" />
          <span><T k="ui.wave.nativeVoice" /></span>
        </div>
        {userWaveform ? (
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-warn border border-warn" />
            <span>Sua Voz (Gravada)</span>
          </div>
        ) : (
          <span className="text-slate-500 font-normal italic lowercase"><T k="ui.wave.recordToCompare" /></span>
        )}
      </div>
    </div>
  );
};

export default AudioWaveformComparator;
